/**
 * Auto-reload once when a dynamic-import chunk or hashed dist/ asset
 * fails to load. Parcel rewrites all code-split chunks to hashed
 * filenames (see package.json parcel-namer-rewrite), and Pagely
 * deploys replace dist/ wholesale — so tabs open across a deploy,
 * or pages served from a stale Cloudflare HTML cache, reference
 * hashes that no longer exist on disk.
 *
 * Gated by sessionStorage so a genuinely missing chunk can't loop.
 *
 * Duplicate-event suppression for the $exception kill metric lives in
 * util/posthog.js's `before_send` hook, which calls `shouldDropChunkException`
 * (exported below). posthog-js captures unhandled rejections via
 * `window.onunhandledrejection` (property assignment) rather than
 * addEventListener, so preventDefault/stopImmediatePropagation in the
 * listeners here cannot stop PostHog from sending the event — only
 * before_send can. See MTN-228 / MTN-366.
 */

import { capture } from "./posthog";

const RELOAD_FLAG_KEY = "mtn-chunk-reload-attempted";
const STALE_CHUNK_MESSAGE =
  /Failed to fetch dynamically imported module|Importing a module script failed|^Cannot find module '[^']+'$/i;
const DIST_ASSET = /\/dist\/[^/?#]+\.(?:js|mjs|css)(?:\?|#|$)/i;

let reloaded = (() => {
  try {
    return sessionStorage.getItem(RELOAD_FLAG_KEY) === "1";
  } catch {
    return false;
  }
})();

// In-memory; once true, `before_send` drops parallel chunk failures from the
// same page load so PostHog only sees the first one. Distinct from `reloaded`,
// which persists across page loads — a post-reload page where the chunk still
// fails gets one real kill-metric event, then `postReloadFailureReported`
// drops the same-load duplicates too.
let reloadInFlight = false;
let postReloadFailureReported = false;

const reload = (reason) => {
  if (reloaded) {
    return false;
  }
  // Persist the loop guard before doing anything reload-adjacent. If storage
  // throws (private mode, disabled), skip the reload — a broken page is
  // better than a reload loop we can't guard across page loads.
  try {
    sessionStorage.setItem(RELOAD_FLAG_KEY, "1");
  } catch {
    reloaded = true;
    reloadInFlight = true;
    return false;
  }
  reloaded = true;
  reloadInFlight = true;
  try {
    capture("chunk_reload_triggered", { reason });
  } catch {
    // Swallow — we're reloading anyway.
  }
  // Small delay so the capture request has a chance to leave the tab.
  setTimeout(() => window.location.reload(), 50);
  return true;
};

/**
 * Called from util/posthog.js `before_send`. Returns true if PostHog should
 * drop this $exception event as a chunk-reload duplicate.
 *
 * In practice the addEventListener handler below runs before posthog-js's
 * `window.onunhandledrejection` property handler fires `before_send`:
 * main.js calls `installChunkReloadHandler()` before `initPostHog()`, and
 * posthog-js further delays the property assignment by lazy-loading the
 * exception-autocapture bundle from CDN. By the spec, IDL-attribute handlers
 * fire at the position of their most recent assignment relative to existing
 * addEventListener listeners — so the order is not guaranteed in the abstract.
 * The branches below are defensive in both orders: if the property handler
 * somehow fires first on a fresh page (reloadInFlight still false), the
 * final branch drops conservatively because we're about to reload anyway.
 *
 * @param {unknown} exceptionValue - the $exception_list[0].value string
 * @returns {boolean} true if before_send should return null for this event
 */
export const shouldDropChunkException = (exceptionValue) => {
  if (!STALE_CHUNK_MESSAGE.test(String(exceptionValue))) {
    return false;
  }
  // Same-page-load duplicates after reload() has armed. The kill metric on
  // this page is the `chunk_reload_triggered` event, not the underlying
  // exceptions.
  if (reloadInFlight) {
    return true;
  }
  // Post-reload page where the chunk STILL fails. Let the first event through
  // as the genuine "reload didn't fix it" kill metric; drop the rest.
  if (reloaded) {
    if (postReloadFailureReported) {
      return true;
    }
    postReloadFailureReported = true;
    return false;
  }
  // Fresh page where the `unhandledrejection` handler has not yet run for this
  // chunk. The handler fires reload() immediately after; later events
  // hit the `reloadInFlight` branch above. Drop conservatively — we're about
  // to reload, the exception itself has no diagnostic value here.
  return true;
};

export const install = () => {
  window.addEventListener("unhandledrejection", (event) => {
    const message = event.reason?.message ?? event.reason ?? "";
    if (!STALE_CHUNK_MESSAGE.test(String(message))) {
      return;
    }
    // Silence the browser's "Unhandled Promise Rejection" console warning.
    // Does NOT stop posthog-js (it captures via window.onunhandledrejection,
    // a property assignment) — that suppression is in before_send via
    // shouldDropChunkException above.
    event.preventDefault();
    if (reloaded) {
      return;
    }
    reload("unhandledrejection");
  });

  // <link>/<script> load failures don't bubble — listen on capture phase.
  // posthog-js's autocapture doesn't observe these (window.onerror only fires
  // for script execution errors, not resource load failures), so this listener
  // needs no suppression beyond the reload-loop guard.
  window.addEventListener(
    "error",
    (event) => {
      const { target } = event;
      if (!target || target === window) {
        return;
      }
      const url = target.href ?? target.src ?? "";
      if (!DIST_ASSET.test(url)) {
        return;
      }
      if (reloaded) {
        return;
      }
      reload("resource_error");
    },
    true
  );
};
